<template>
  <div class="viewer-container" :class="selectedMenu">
    <!-- Show back button in main menu -->
    <BackButton v-if="selectedMenu === ''" :go-back="back" />
    <!-- Show confirm button in detail view -->
    <ConfirmButton v-if="isDetail && selectedMenu === ''" :go-back="handleExportAvatar" />
    <!-- Asset list - displayed in detail view and when asset menu is selected -->
    <AssetList
        v-if="isDetail && (selectedMenu === '' || selectedMenu === 'asset')"
        :panel-visible="hairPanelVisible"
        :hair-clicked="assetSelected"
        :hair-color-changed="colorSelected"
        :toggle="assetOpen"
        :list="assetList">
    </AssetList>
    <!-- Body list - displayed in detail view and when body menu is selected -->
    <BodyList
        v-if="isDetail && (selectedMenu === '' || selectedMenu === 'body')"
        :class="selectedMenu === 'body' ? 'active' : ''"
        :panel-visible="bodyPanelVisible"
        :body-clicked="bodySelected"
        :toggle="bodyOpen"
        :list="bodyList">
    </BodyList>

    <!-- Container for 3D scene iframe -->
    <div ref="scene" class="iframe-container"></div>
    <!-- Loading spinner shown during loading -->
    <div v-if="isLoading" class="spinner-wrapper2">
      <div class="spinner2"></div>
      <MatchyLogo position="center" size="large" />
    </div>
  </div>
</template>

<script>
// Import of required components and libraries
import MatchyLogo from "@/components/MatchyLogo.vue";
import { AvaturnSDK } from '@avaturn/sdk';
import { markRaw } from 'vue';
import BackButton from "@/components/BackButton.vue";
import ConfirmButton from "@/components/ConfirmButton.vue";
import AssetList from "@/components/AssetList.vue";
import BodyList from "@/components/BodyList.vue";
import { API } from '@/constants/urls';

/**
* AvaturnViewer
* 
* Component for viewing and editing avatars using Avaturn SDK
* Handles body types, assets, and avatar export
* 
* @component
*/
export default {
// Component registration
components: {
  BodyList,
  AssetList,
  BackButton,
  ConfirmButton,
  MatchyLogo
},
// Component props
props: {
  /** URL for the Avaturn editor */
  url: {
    type: String,
    default: null
  },
  /** Function to navigate back */
  goBack: Function,
  /** Callback when avatar is loaded */
  avatarLoaded: Function,
  /** Function to reset the component */
  reset: Function
},
// Component local state
data() {
  return {
    /** Avaturn SDK instance */
    sdk: null,
    /** Asset panel open state */
    assetOpen: false,
    /** Body panel open state */
    bodyOpen: false,
    /** List of available body types */
    bodyList: [],
    /** List of available assets */
    assetList: [],
    /** Loading state */
    isLoading: false,
    /** Detail view state */
    isDetail: false,
    /** Avatar gender */
    gender: null,
    /** Selected menu */
    selectedMenu: '',
    /** API token */
    token: API.TOKEN,
    /** Filter IDs for special assets */
    filterIds: [
      "018fce86-26e1-754c-b352-ac4a8e12ef41",
      "018fce85-e18e-7503-8896-38264e1c9761"
    ],
    /** Clothing IDs */
    clothIds: [
      "018fcea4-dd59-772d-973b-56946af57455",
      "018fcea4-4f99-7ead-810c-eeb9b6ef4351",
    ],
  };
},
// Initialize SDK when component is mounted
async mounted() {
  await this.initSdk();
},
// Clean up when component is unmounted
beforeUnmount() {
  // Any cleanup logic would go here
},
// Computed properties
computed: {
  /**
   * Filtered items excluding special assets
   * @returns {Array} Filtered asset list
   */
  filteredItems() {
    return this.assetList.filter(
        (item) => !this.filterIds.includes(item.id) && !this.clothIds.includes(item.id)
    );
  },
  /**
   * Empty assets (special filter assets)
   * @returns {Array} Empty asset list
   */
  emptyItems() {
    return this.assetList.filter((item) => this.filterIds.includes(item.id));
  },
  /**
   * Clothing assets
   * @returns {Array} Clothing asset list
   */
  clothItems() {
    return this.assetList.filter((item) => this.clothIds.includes(item.id));
  },
},
methods: {
  /**
   * Controls body panel visibility
   * @param {boolean} value - Visibility state
   */
  bodyPanelVisible(value) {
    if (value) {
      this.selectedMenu = 'body';
    } else {
      this.selectedMenu = '';
    }
    this.bodyOpen = value;
    if (value && this.assetOpen) {
      this.assetOpen = false;
    }
  },
  /**
   * Changes hair color
   * @param {string} color - Color value
   */
  colorSelected(color) {
    console.log('sdk', this.sdk, color);
    this.sdk.setParam("hair_color", color);
  },
  /**
   * Controls hair panel visibility
   * @param {boolean} value - Visibility state
   */
  hairPanelVisible(value) {
    if (value) {
      this.selectedMenu = 'asset';
    } else {
      this.selectedMenu = '';
    }
    this.assetOpen = value;
    console.log('hair is open', value, this.bodyOpen);
    if (value && this.bodyOpen) {
      this.bodyOpen = false;
    }
  },
  /**
   * Changes body type
   * @param {Object} body - Body type object
   */
  bodySelected(body) {
    this.sdk.setActiveBody(body.id).then(() => {
      console.log(`Body changed: ${body.id}`);
    });
  },
  /**
   * Handles back navigation
   */
  back() {
    console.log('vars', this.bodyOpen, this.assetOpen, this.isDetail);
    if (this.bodyOpen) {
      this.bodyOpen = false;
    } else if (this.assetOpen) {
      this.assetOpen = false;
    } else {
      this.goBack('list');
    }
  },
  /**
   * Handles avatar export process
   */
  async handleExportAvatar() {
    this.isLoading = true;
    await this.assetSelected(this.emptyItems.pop());
    setTimeout(() => {
      console.log(`Calling sdk.exportAvatar()`);
      this.sdk.exportAvatar().then((res) => {
        console.log(res);
        console.log("[then] Avatar exported: ", res);
        this.isLoading = false;
        this.avatarLoaded(res);
      }).catch(() => {
        this.isLoading = false;
      });
    }, 500);
  },
  /**
   * Selects an asset
   * @param {Object} asset - Asset to select
   */
  async assetSelected(asset) {
    try {
      if (asset) {
        await this.sdk.setActiveAsset(asset.id);
        console.log(`Asset changed: ${asset.id}`);
      }
    } catch (error) {
      console.error(`Error changing asset ${asset.id}:`, error);
      throw error;
    }
  },
  /**
   * Initializes Avaturn SDK and configures it
   */
  async initSdk() {
    this.isLoading = false;
    this.sdk = markRaw(new AvaturnSDK());
    this.sdk.init(this.$refs.scene, {
      url: this.url,
      iframeClassName: 'iframe-wrapper', 
      token: this.token,
      disableUi: true // Disable default UI
    }).then((result) => {
      console.log('loaded', result);
      this.isLoading = false;
      // Event listener triggered when SDK is loaded
      this.sdk.on("load", (data) => {
        console.log("[callback] load: ", data);
        // Get current body types
        this.sdk.getBodyList().then((list) => {
          console.log('body', list);
          // Filter out body_2 type
          this.bodyList = list.filter(item => item.id !== 'body_2');
          // Store first body type's gender
          this.gender = list[0].gender;
          this.isDetail = true;
          // Get current assets (clothes, hair, etc.)
          this.sdk.getAssetList().then(async (list) => {
            this.assetList = list;
            console.log('assets', list);
            // Apply first clothing to avatar
            await this.assetSelected(this.clothItems.pop());
          });
        });
      });
    }).catch((reason) => {
        // Catch and log SDK initialization errors
        console.log(`[Avaturn SDK Error]: ${reason}`);
        this.isLoading = false;
      });
  },
}
};
</script>